import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { displaySize } from '@/theme';

import { ExpandableLede } from './ExpandableLede';

/**
 * The heading's size on a phone, and why it leaves the type scale.
 *
 * The scale's `h2` bottoms out at 31px, which is the right size for a heading
 * that introduces a band of a page somebody is already reading. On a phone it
 * was doing a different job and doing it badly: 31px over a 24ch measure sets
 * "Two answers: what URA values your car at, and what URA charges" on four
 * lines, the lede under it ran to six more, and the block came to roughly
 * 280px — which, stacked under an opening band that was itself a heading
 * block, meant two headings and no content in the first screenful.
 *
 * 20px is the scale's `h5`, so this is not an invented size: it is the rung
 * the scale already uses for a heading inside a card. That is the right
 * comparison, because on a phone a section *is* a card — one column, one
 * thing at a time, and the heading's job is to label what follows rather than
 * to open a composition.
 *
 * The line height goes the other way, up from 1.08 to 1.25. Display leading
 * is set for type large enough that the lines look loose at 1.08; at 20px
 * over three lines it reads as a solid block, and the heading needs to be
 * scannable more than it needs to be tight.
 */
const TITLE_SIZE = { xs: '1.25rem', md: displaySize.h2 };
const TITLE_SIZE_H1 = { xs: '1.25rem', md: displaySize.h1 };

type Props = {
  /** Target of the enclosing Section's aria-labelledby. */
  id?: string;
  eyebrow?: string;
  title: string;
  lede?: string;
  align?: 'left' | 'center';
  /** Heading level; sections are h2 unless the page says otherwise. */
  level?: 'h1' | 'h2' | 'h3';
  /** On a dark band. */
  inverse?: boolean;
};

/**
 * The heading block that opens a section of a page: label, title, and the
 * sentence that says what the section is for.
 *
 * It is the same three parts at every width and a different proportion of the
 * screen at each. Everything about the phone case is a breakpoint inside a
 * value rather than a second tree, because the site prerenders with
 * `ssr: false` and the HTML is written once for every width — see
 * pageIntroBand.ts for the full version of that constraint.
 */
export function SectionHeading({
  id,
  eyebrow,
  title,
  lede,
  align = 'left',
  level = 'h2',
  inverse,
}: Props) {
  const centered = align === 'center';
  return (
    <Stack
      spacing={{ xs: 2, md: 3 }}
      sx={{
        mb: { xs: 6, md: 12 },
        textAlign: align,
        alignItems: centered ? 'center' : 'flex-start',
      }}
    >
      {eyebrow && (
        <Typography variant="overline" sx={{ color: inverse ? 'primary.light' : 'primary.main' }}>
          {eyebrow}
        </Typography>
      )}
      <Typography
        id={id}
        variant={level === 'h1' ? 'h1' : 'h2'}
        component={level}
        sx={{
          maxWidth: '22ch',
          fontSize: level === 'h1' ? TITLE_SIZE_H1 : TITLE_SIZE,
          lineHeight: { xs: 1.25, md: 1.08 },
        }}
      >
        {title}
      </Typography>
      {lede && (
        <ExpandableLede
          text={lede}
          maxWidth="60ch"
          color={inverse ? 'rgba(255,255,255,0.78)' : 'text.secondary'}
          {...(inverse ? { inverse } : {})}
        />
      )}
    </Stack>
  );
}
